"use client";

import Image from "next/image";
import type Project from "@/types/project";
import { ArrowLeft, ChevronLeft, ChevronRight, ExternalLink, GitBranch, Layers } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StackBadges from "@/components/common/StackBadges";
import Link from "next/link";

const SWIPE_THRESHOLD_PX = 48;
const SWIPE_MAX_DRAG_PX = 140;
const WHEEL_ACCUM_THRESHOLD = 65;
const WHEEL_ACCUM_RESET_MS = 180;
const SLIDE_TRANSITION_MS = 480;

type ProjectShowcaseProps = {
  project: Project;
};

function projectSlides(project: Project): string[] {
  const shots = project.screenshots?.filter(Boolean) ?? [];
  if (shots.length) return shots;
  if (project.image) return [project.image];
  return [];
}

export default function ProjectShowcase({ project }: ProjectShowcaseProps) {
  const slides = useMemo(() => projectSlides(project), [project]);
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const pointerIdRef = useRef<number | null>(null);
  const startClientXRef = useRef(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const wheelAccumRef = useRef(0);
  const wheelIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const total = slides.length;
  const safeIndex = total > 0 ? ((index % total) + total) % total : 0;

  const go = useCallback(
    (delta: number) => {
      setIndex((i) => {
        const next = i + delta;
        if (next < 0) return total - 1;
        if (next >= total) return 0;
        return next;
      });
    },
    [total]
  );

  useEffect(() => {
    setIndex(0);
    setDragX(0);
    wheelAccumRef.current = 0;
  }, [project.id]);

  useEffect(() => {
    const el = sliderRef.current;
    if (!el || total <= 1) return;

    const bumpWheelIdleReset = () => {
      if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current);
      wheelIdleTimerRef.current = setTimeout(() => {
        wheelAccumRef.current = 0;
        wheelIdleTimerRef.current = null;
      }, WHEEL_ACCUM_RESET_MS);
    };

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;

      let dx = 0;
      if (e.shiftKey) {
        dx = e.deltaY;
      } else {
        const absX = Math.abs(e.deltaX);
        const absY = Math.abs(e.deltaY);
        if (absX >= absY && absX > 0.5) {
          dx = e.deltaX;
        } else {
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();

      wheelAccumRef.current += dx;
      bumpWheelIdleReset();

      if (wheelAccumRef.current > WHEEL_ACCUM_THRESHOLD) {
        wheelAccumRef.current = 0;
        go(1);
      } else if (wheelAccumRef.current < -WHEEL_ACCUM_THRESHOLD) {
        wheelAccumRef.current = 0;
        go(-1);
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current);
      wheelAccumRef.current = 0;
    };
  }, [total, go, project.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const clampDrag = useCallback((dx: number) => {
    const t = Math.min(Math.abs(dx) / SWIPE_MAX_DRAG_PX, 1);
    const eased = 1 - (1 - t) ** 2;
    const sign = dx < 0 ? -1 : 1;
    return sign * eased * SWIPE_MAX_DRAG_PX;
  }, []);

  const endPointerSwipe = useCallback(
    (clientX: number, pointerId: number) => {
      const el = sliderRef.current;
      if (el && pointerIdRef.current === pointerId) {
        try {
          el.releasePointerCapture(pointerId);
        } catch {
          /* déjà relâché */
        }
      }
      pointerIdRef.current = null;
      setIsDragging(false);

      const dx = clientX - startClientXRef.current;
      setDragX(0);
      if (total <= 1) return;
      if (dx > SWIPE_THRESHOLD_PX) go(-1);
      else if (dx < -SWIPE_THRESHOLD_PX) go(1);
    },
    [go, total]
  );

  const onSliderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (total <= 1) return;
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;

    pointerIdRef.current = e.pointerId;
    startClientXRef.current = e.clientX;
    setIsDragging(true);
    setDragX(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onSliderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId || total <= 1) return;
    const raw = e.clientX - startClientXRef.current;
    setDragX(clampDrag(raw));
  };

  const onSliderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    endPointerSwipe(e.clientX, e.pointerId);
  };

  const onSliderPointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    endPointerSwipe(e.clientX, e.pointerId);
  };

  const onSliderLostPointerCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    pointerIdRef.current = null;
    setIsDragging(false);
    setDragX(0);
  };

  const longText = project.longDescription ?? project.description;
  const sourceHref = project.sourceUrl;
  const deployHref = project.deployUrl;

  const slidePercent = total > 0 ? (safeIndex * 100) / total : 0;
  const trackTransform =
    total <= 1 ? undefined : `translateX(calc(-${slidePercent}% + ${dragX}px))`;

  const linkPill =
    "inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-2.5 text-xs font-semibold text-on-surface transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary";

  return (
    <>
      <article
        id={project.anchor}
        className="relative space-y-6"
      >
        <Link href="/projets" className="inline-flex items-center gap-2 text-sm font-mono text-on-surface-variant hover:text-white transition-colors group mb-4">
          <ArrowLeft className="size-4" aria-hidden />
          Retour
        </Link>
        {/* header */}
        <header>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="space-y-3">
              <h2 className="font-headline text-3xl font-bold tracking-tight text-on-surface md:text-4xl lg:text-[2.75rem] lg:leading-tight">
                {project.title}
              </h2>
            </div>
          </div>
        </header>

        {/* carousel */}
        <div>
          <div className="mb-4 flex items-center justify-between gap-3">
            <span className="text-[10px] font-mono text-on-surface-variant">
              {String(safeIndex + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} IMAGE{String(total) === "1" ? "" : "S"}
            </span>
          </div>

          <div
            ref={sliderRef}
            className={`group/slider relative aspect-video w-full touch-pan-y overflow-hidden rounded-2xl border border-white/10 bg-black/40 select-none ${total > 1 ? "cursor-grab active:cursor-grabbing" : ""
              }`}
            role="region"
            aria-roledescription="carrousel"
            aria-label={`Captures du projet ${project.title}`}
            onPointerDown={onSliderPointerDown}
            onPointerMove={onSliderPointerMove}
            onPointerUp={onSliderPointerUp}
            onPointerCancel={onSliderPointerCancel}
            onLostPointerCapture={onSliderLostPointerCapture}
          >
            <div
              className={`flex h-full will-change-transform ${total > 1 && !isDragging ? "transition-transform ease-[cubic-bezier(0.25,0.8,0.25,1)]" : ""}`}
              style={{
                width: total > 0 ? `${total * 100}%` : "100%",
                transform: trackTransform,
                transitionDuration: total > 1 && !isDragging ? `${SLIDE_TRANSITION_MS}ms` : undefined,
              }}
            >
              {slides.map((src, i) => (
                <div
                  key={`${project.id}-slide-${i}`}
                  className="relative h-full shrink-0 overflow-hidden"
                  style={{ width: total > 0 ? `${100 / total}%` : "100%" }}
                >
                  {src ? (
                    <Image
                      src={src}
                      alt={`${project.title} — capture ${i + 1}`}
                      fill
                      sizes="(max-width: 1024px) 100vw, min(928px, 85vw)"
                      className="object-cover"
                      draggable={false}
                      priority={i === 0}
                      quality={75}
                    />
                  ) : null}
                </div>
              ))}
            </div>
            {/* <div className="pointer-events-none absolute inset-0 z-10 bg-linear-to-t from-black/50 via-transparent to-black/20" /> */}

            {total > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  className="pointer-events-auto absolute left-3 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/50 text-on-surface backdrop-blur-md transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                  aria-label="Image précédente"
                >
                  <ChevronLeft className="size-5" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  className="pointer-events-auto absolute right-3 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/50 text-on-surface backdrop-blur-md transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary"
                  aria-label="Image suivante"
                >
                  <ChevronRight className="size-5" />
                </button>
                <div className="pointer-events-auto absolute bottom-4 left-0 right-0 z-20 flex justify-center gap-2">
                  {slides.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${i === safeIndex ? "w-8 bg-primary" : "w-1.5 bg-white/25 hover:bg-white/45"
                        }`}
                      aria-label={`Aller à la capture ${i + 1}`}
                      aria-current={i === safeIndex}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="">
          <div className="space-y-10">
            <section className="space-y-4">
              <p className="text-sm leading-relaxed text-on-surface-variant md:text-[15px]">{longText}</p>
              {project.highlights?.length ? (
                <ul className="space-y-2.5 border-t border-white/10 pt-4">
                  {project.highlights.map((item) => (
                    <li key={item} className="flex items-center gap-2.5 text-sm text-on-surface-variant">
                      <span className="text-white/80" aria-hidden>-</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>

            <section className="space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-on-surface">Stack</h3>
              <StackBadges names={project.tags} />
            </section>

            {
              sourceHref || deployHref ? (
                <section className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-on-surface">Liens</h3>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    {sourceHref && (
                      <Link className={linkPill} href={sourceHref} target="_blank" rel="noopener noreferrer">
                        <GitBranch className="size-4 opacity-80" aria-hidden />
                        Code source
                      </Link>
                    )}
                    {deployHref && (
                      <Link className={linkPill} href={deployHref} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="size-4 opacity-80" aria-hidden />
                        Voir le site en ligne
                      </Link>
                    )}
                  </div>
                </section>
              ) : null
            }

          </div>
        </div>
      </article>
    </>

  );
}
